import { usePapaParse } from 'react-papaparse'

/**
 * Names papaparse gives to columns whose header is blank.
 *
 * A trailing delimiter in an uploaded file produces a column with no header. papaparse leaves the
 * first one named "" and, because the rest duplicate it, renames them "_1", "_2", ... (see the
 * duplicate-header block in papaparse.js). Those generated names only mean "blank" when a blank
 * header is actually present — "_1" is a legitimate column name in its own right.
 *
 * This is decided after parsing rather than with papaparse's `transformHeader` option. The parse
 * runs in a Web Worker, and function options cannot be structure-cloned into it: passing one threw
 * `DataCloneError: ... could not be cloned` before parsing began, leaving the upload promise
 * unresolved so every upload silently did nothing (SWT-1622).
 */
export const blankHeaderNames = (fields: string[] = []): Set<string> => {
  const blanks = fields.filter((field) => !field || !field.trim())
  if (blanks.length === 0) return new Set()
  return new Set([...blanks, ...fields.filter((field) => /^_\d+$/.test(field))])
}

/**
 * Removes columns that came from a blank header, plus papaparse's `__parsed_extra` bucket, which
 * collects values from rows carrying more fields than the header declared. Neither is anything the
 * user asked for, and downstream they become nameless CATEGORY_<i>_ fields that render as blank
 * entries in the cell visualization dropdowns.
 */
export const stripBlankHeaderColumns = (
  rows: Record<string, unknown>[],
  fields?: string[]
): Record<string, unknown>[] => {
  const blank = blankHeaderNames(fields)
  if (blank.size === 0) {
    return rows.map((row) =>
      '__parsed_extra' in row
        ? Object.fromEntries(Object.entries(row).filter(([name]) => name !== '__parsed_extra'))
        : row
    )
  }
  return rows.map((row) =>
    Object.fromEntries(
      Object.entries(row).filter(([name]) => !blank.has(name) && name !== '__parsed_extra')
    )
  )
}

export const useReadCSV = (): ((_: string) => Promise<unknown>) => {
  const { readString } = usePapaParse()

  const readCSV = async (csvData: string) => {
    return new Promise((resolve) => {
      readString(csvData, {
        // Keep the parse off the main thread: cell data uploads carry one row per cell.
        // Nothing passed in this config may be a function, or the worker handoff throws.
        worker: true,
        header: true,
        complete: (results) => {
          resolve(
            stripBlankHeaderColumns(
              results.data as Record<string, unknown>[],
              results.meta?.fields as string[] | undefined
            )
          )
        },
      })
    })
  }
  return readCSV
}

export default useReadCSV
